import React, { useEffect } from "react";
import {
  UseFormWatch,
  Control,
  UseFormRegister,
  useFieldArray,
  FieldErrors,
  UseFormReset,
  UseFormSetValue,
} from "react-hook-form";
import { FormGroup, Label, Col, Row, Button, Table } from "reactstrap";
import { AiOutlineCopy, AiOutlineDelete } from "react-icons/ai";

import { FreightItemForm } from "@/@types/ShippingItem";
import { getNewItems } from "@/utils/common-functions";
import upsLTLHandlingUnits from "@/data/carriers/upsLTLHandlingUnits.json";
import FreightGoodTypes from "@/data/carriers/FreightGoodTypes.json";
import FTLVehicleTypes from "@/data/carriers/FTLVehicleTypes.json";
import classes from "@/styles/get-quotes/get-quotes-main.module.css";

type FreightItemsProps = {
  watch: UseFormWatch<FreightItemForm>;
  control: Control<FreightItemForm, any>;
  errors: FieldErrors<FreightItemForm>;
  register: UseFormRegister<FreightItemForm>;
  reset: UseFormReset<FreightItemForm>;
  setValue: UseFormSetValue<FreightItemForm>;
  isFTL?: boolean;
};

const FreightItems = ({
  errors,
  watch,
  control,
  register,
  reset: resetField,
  setValue,
  isFTL,
}: FreightItemsProps) => {
  const uom = watch("unitOfMeasurement");
  const { fields, append, insert, remove } = useFieldArray({
    control,
    name: "freightItems",
  });

  const onAddCopy = (index: number) => {
    // Get the item at the given index
    const itemToCopy = watch("freightItems")[index];

    // Create a copy of the item
    const newItem = { ...itemToCopy };
    // Append the new item to the array
    insert(index, newItem);
  };

  const handleRowItems = ({ target }: any) => {
    const rowItemCount = parseInt(target.value) - fields.length;
    if (rowItemCount > 0) {
      append(getNewItems(parseInt(target.value) - fields.length));
    } else {
      const slicedItems = watch("freightItems").slice(0, target.value);

      resetField({
        freightItems: [],
      });

      append(slicedItems);
    }
  };

  useEffect(() => {
    const items = [...watch("freightItems")];

    for (let i = 0; i < items.length; i++) {
      const element = items[i];
      if (uom === "IMPERIAL") {
        element["weight"] = `${(
          parseFloat(element["weight"]) * 2.2 || 0
        ).toFixed(2)}`;
        element["height"] = `${(
          parseFloat(element["height"] ?? "0") / 2.54 || 0
        ).toFixed(1)}`;
        element["width"] = `${(
          parseFloat(element["width"] ?? "0") / 2.54 || 0
        ).toFixed(1)}`;
        element["length"] = `${(
          parseFloat(element["length"] ?? "0") / 2.54 || 0
        ).toFixed(1)}`;
      }

      if (uom === "METRIC") {
        element["weight"] = `${(
          parseFloat(element["weight"]) / 2.2 || 0
        ).toFixed(2)}`;
        element["height"] = `${(
          parseFloat(element["height"] ?? "0") * 2.54 || 0
        ).toFixed(1)}`;
        element["width"] = `${(
          parseFloat(element["width"] ?? "0") * 2.54 || 0
        ).toFixed(1)}`;
        element["length"] = `${(
          parseFloat(element["length"] ?? "0") * 2.54 || 0
        ).toFixed(1)}`;
      }
    }

    setValue("freightItems", items);
  }, [setValue, uom, watch]);

  return (
    <>
      <Row>
        {/* <Col sm={4}>
          <InputGroup
            required
            label="Email Address"
            name="guestEmail"
            register={register}
            error={errors.guestEmail?.message}
          />
        </Col> */}
        <FormGroup row>
          <Label sm={2}>Row of Items</Label>
          <Col sm={2}>
            <select
              className="form-select"
              name="rowOfItems"
              onChange={handleRowItems}
            >
              {new Array(25).fill(0).map((_, i) => (
                <option value={i + 1} key={i}>
                  {i + 1}
                </option>
              ))}
            </select>
            <br />
          </Col>
          <Col className="d-flex align-items-center justify-content-end gap-2">
            <p className="m-0 fw-bold">Measurement Unit</p>
            <div className={classes.unitBtnWrapper}>
              <label
                htmlFor="fr-METRIC"
                className={`btn btn-light ${classes.unitBtn} ${
                  classes.btnBorderLeft
                } ${uom === "METRIC" && classes.unitBtnActive}`}
              >
                METRIC
                <input
                  id="fr-METRIC"
                  type="radio"
                  {...register("unitOfMeasurement", { required: true })}
                  value="METRIC"
                  className="d-none"
                />
              </label>
              <label
                htmlFor="fr-IMPERIAL"
                className={`btn btn-light ${classes.unitBtn} ${
                  classes.btnBorderRight
                } ${uom === "IMPERIAL" && classes.unitBtnActive}`}
              >
                IMPERIAL
                <input
                  id="fr-IMPERIAL"
                  type="radio"
                  {...register("unitOfMeasurement", { required: true })}
                  value="IMPERIAL"
                  className="d-none"
                />
              </label>
            </div>
          </Col>
        </FormGroup>
      </Row>

      <Table responsive>
        <thead className={classes.thead}>
          <tr>
            <th>#</th>
            <th className="miw-150px">
              Quantity <span className="text-danger">*</span>
            </th>
            <th className="miw-150px">
              Length - {uom === "METRIC" ? "cm(s)" : "in(s)"}{" "}
              <span className="text-danger">*</span>
            </th>
            <th className="miw-150px">
              Width - {uom === "METRIC" ? "cm(s)" : "in(s)"}{" "}
              <span className="text-danger">*</span>
            </th>
            <th className="miw-150px">
              Height - {uom === "METRIC" ? "cm(s)" : "in(s)"}{" "}
              <span className="text-danger">*</span>
            </th>
            <th className="miw-150px">
              Weight - {uom === "METRIC" ? "kg(s)" : "lb(s)"}
              <span className="text-danger">*</span>
            </th>
            {isFTL ? (
              <>
                <th className="miw-150px">
                  Vehicle Type<span className="text-danger">*</span>
                </th>
                <th className="miw-150px">
                  Type of Goods <span className="text-danger">*</span>
                </th>
              </>
            ) : (
              <>
                <th className="miw-150px">
                  Package Type<span className="text-danger">*</span>
                </th>
                <th className="miw-150px">
                  Description <span className="text-danger">*</span>
                </th>
              </>
            )}
            {/* <th className="miw-150px">
              Is Dangerous <span className="text-danger">*</span>
              <MdHelp id="isDangerousTooltip" />
              <UncontrolledTooltip target="isDangerousTooltip">
                Is dangerous goods included?
              </UncontrolledTooltip>
            </th> */}
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {fields.map((item, index) => (
            <tr key={item.id}>
              <td>{index + 1}</td>
              <td className="miw-150px">
                <select
                  className="form-select"
                  {...register(`freightItems.${index}.quantity`)}
                >
                  {new Array(25).fill(0).map((_, i) => (
                    <option value={i + 1} key={i}>
                      {i + 1}
                    </option>
                  ))}
                </select>
              </td>
              <td>
                <input
                  className="form-control"
                  type="text"
                  {...register(`freightItems.${index}.length`, {
                    required: true,
                  })}
                />
              </td>
              <td>
                <input
                  className="form-control"
                  type="text"
                  {...register(`freightItems.${index}.width`, {
                    required: true,
                  })}
                />
              </td>
              <td>
                <input
                  className="form-control"
                  type="text"
                  {...register(`freightItems.${index}.height`, {
                    required: true,
                  })}
                />
              </td>
              <td>
                <input
                  className="form-control"
                  type="text"
                  {...register(`freightItems.${index}.weight`, {
                    required: true,
                  })}
                />
              </td>
              {isFTL ? (
                <>
                  <td className="miw-150px">
                    <select
                      className="form-select"
                      {...register(`freightItems.${index}.vehicleType`)}
                    >
                      {FTLVehicleTypes.map((u, i) => (
                        <option key={i} value={u.code}>
                          {u.name}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="miw-150px">
                    <select
                      className="form-select"
                      {...register(`freightItems.${index}.typeOfGoods`)}
                    >
                      {FreightGoodTypes.map((u, i) => (
                        <option key={i} value={u.code}>
                          {u.name}
                        </option>
                      ))}
                    </select>
                  </td>
                </>
              ) : (
                <>
                  <td className="miw-150px">
                    <select
                      className="form-select"
                      {...register(`freightItems.${index}.packagingTypeLTL`)}
                    >
                      {upsLTLHandlingUnits.map((u, i) => (
                        <option key={i} value={u.code}>
                          {u.name}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="miw-150px">
                    <input
                      className="form-control"
                      type="text"
                      {...register(`freightItems.${index}.description`)}
                    />
                  </td>
                </>
              )}
              {/* <td className="text-center miw-150px">
                <input
                  className="form-checkbox"
                  type="checkbox"
                  {...register(`freightItems.${index}.isDangerous`)}
                />
              </td> */}
              <td className="d-flex align-items-center gap-1 miw-150px">
                <Button
                  className="btn-darkBlue text-white d-block"
                  type="button"
                  onClick={() => onAddCopy(index)}
                >
                  <AiOutlineCopy />
                </Button>
                <Button
                  className="d-block"
                  type="button"
                  color="danger"
                  onClick={() => remove(index)}
                >
                  <AiOutlineDelete />
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      <h6>
        Total:{" "}
        {watch("freightItems")
          .reduce((a, b) => a + parseFloat(b.quantity ?? "1"), 0)
          ?.toFixed(2)}{" "}
        Package | :{" "}
        {(
          watch("freightItems").reduce(
            (a, b) => a + parseFloat(b.weight) * parseFloat(b.quantity ?? "1"),
            0
          ) || 0
        )?.toFixed(2)}{" "}
        kg
      </h6>
      <Button
        outline
        className="btnDarkBlue-outlined btn-sm float-end"
        onClick={() => {
          append(getNewItems(1));
        }}
      >
        {" "}
        + Add Item
      </Button>
    </>
  );
};

export default FreightItems;
